import { LCDocumentEditorUtils } from './LCDocumentEditorUtils';
import { LCCommentBlockUtility } from './LCCommentBlockUtility';
import { JSErrorHandling } from './../JSErrorHandling';
import { PostBackHelper } from './../PostBackHelper';
import { StringUtils } from './../StringUtils';
import { SessionHelper } from './../SessionHelper';
import { LectioJSUtils } from './../LectioJSUtils';
import { LectioClientDevSettings } from "./../LectioClientDevSettings";
import { LectioPageOps } from '../LectioPageOps';
import { CKEditorEx, CKEditorLectio, EditorConfig, LCDocumentEditorState } from './Types';

export * from './PluginManager'; //kaldes i lcdocumenteditor.cs - kan det gøres anderledes?
export * from './LCDocumentEditorUtils'; //skal fjernes når lcdocumenteditor.ts er død
export * from './LCCommentBlockUtility'; //bruges i LCDocumentEditor.ts
export * from './LCDocumentEditorService'; //skal fjernes når lcdocumenteditor.ts er død
export * from './EditorAttachedFilePlugin'; //skal fjernes når lcdocumenteditor.ts er død
export * from './LCLinkTitleLoop'; //skal fjernes når lcdocumenteditor.ts er død
export * from './LectioEditorLinkPlugin'; //skal fjernes når lcdocumenteditor.ts er død

export * from './../LC/LCDocumentPresentation';
export * from './../LC/LCDocumentChapterChooser3';

export class LCDocumentEditor {
	private static isGlobalConfigured = false;

	public editorReady: Promise<CKEDITOR.editor>;
	public ckEditor: CKEDITOR.editor;

	constructor(editor: CKEDITOR.editor, editorReady: Promise<CKEDITOR.editor>) {
		this.editorReady = editorReady;
		this.ckEditor = editor;
	}

	private static ConfigureCKEditorGlobal(config: EditorConfig, year: number): void {
		if (LCDocumentEditor.isGlobalConfigured)
			return;
		LCDocumentEditor.isGlobalConfigured = true;

		// Kan vaere praktisk at kunne iterere editorerne.
		CKEDITOR.instances[Symbol.iterator] = function* () {
			for (const id in CKEDITOR.instances)
				yield CKEDITOR.instances[id];
		};

		CKEDITOR.plugins.setLang('oembed', 'da', {
			title: "Indsæt medie indhold (billede, video, lyd)",
			button: "Indsæt medie fra ekstern side",
			pasteUrl: "Indsæt en URL (forkortede URLs understøttes også) fra én af de understøttede sider (f.eks. YouTube, Flickr, Qik, Vimeo, Hulu, Viddler, MyOpera, mm.).",
			invalidUrl: "Angiv en gyldig URL.",
			noEmbedCode: "Ingen indsat kode fundet, eller siden er ikke understøttet.",
			url: "URL:",
			width: "Bredde:",
			height: "Højde:",
			widthTitle: "Bredde af det indsatte indhold",
			heightTitle: "Højde af det indsatte indhold",
			maxWidth: "Max. bredde:",
			maxHeight: "Max. højde:",
			maxWidthTitle: "Maximal bredde af det indsatte indhold",
			maxHeightTitle: "Maximal højde af det indsatte indhold",
			none: 'Ingen',
			resizeType: "Størrelsesændring (kun videoer):",
			noresize: "Ingen (brug standard)",
			responsive: "Responsiv størrelse",
			custom: "Specifik størrelse",
			noVimeo: "Ejeren af denne video har sat domain restriktioner, og du vil ikke kunne indsætte den i dit forløbsmateriale.",
			Error: "Medie indhold kunne ikke hentes, prøv venligst en anden URL."
		});

		// Der er oversaettelse med, men i det optimerede build bliver den ikke
		// fundet/indlaest, saa der kommer fejl ved drop af billede.
		CKEDITOR.plugins.setLang('uploadwidget', 'da', {
			abort: 'Upload er afbrudt af brugeren.',
			doneOne: 'Filen er uploadet.',
			doneMany: 'Du har uploadet %1 filer.',
			uploadOne: 'Uploader fil ({percentage}%)...',
			uploadMany: 'Uploader filer, {current} af {max} er uploadet ({percentage}%)...'
		});

		CKEDITOR.plugins.setLang('openlink', 'da', {
			menu: 'Åbn i ny tab'
		});

		LCDocumentEditorUtils.Year = year;

		CKEDITOR.on('dialogDefinition', ev => {
			const dialogName = ev.data.name;
			const dialogDefinition = ev.data.definition as CKEDITOR.dialog.IDialogDefinition;

			if (dialogName === 'image2') {
				const infoTab = (dialogDefinition as any).getContents!('info');
				infoTab.get!('alt').style = 'display: none';
				infoTab.get!('src').style = 'display: none';
				(infoTab.get!('hasCaption') as CKEDITOR.dialog.definition.labeledElement).label =
					'Tilføj en billedtekst';
			}

			if (dialogName === 'mathjax') {
				const mathJaxInfoTab = (dialogDefinition as any).getContents!('info');
				if (mathJaxInfoTab) {
					const t = mathJaxInfoTab.get!('equation') as CKEDITOR.dialog.definition.textInput;
					if (t) {
						t.label = 'Skriv dit TeX-indhold her:'; //Der er fejl i den danske oversættelse, så vi gør lige det her
					}
				}
			}
		});
	}

	private SetupAutoBackup(lectioConfig: EditorConfig) {
		if (!lectioConfig.autoBackupUrl)
			return;

		this.lectioConfig = lectioConfig;
		this.ScheduleNextAutobackup();
	}

	private DoAutobackup(): Promise<undefined> {
		LCDocumentEditor.Log('Autobackup start');

		const currentEditorContent = this.ckEditor.getData();
		let isDirty: boolean;
		if (this.autobackupPreviousEditorContent) {
			// Vi holder styr på om indholdet har ændret sig siden sidste backup
			isDirty = currentEditorContent !== this.autobackupPreviousEditorContent;
		} else {
			isDirty = this.ckEditor.checkDirtyLectio();
		}

		if (!isDirty) {
			this.ScheduleNextAutobackup();
			const def = LectioJSUtils.CreateDeferred();
			def.resolve(undefined);
			return def.promise();
		}

		const xhr = $.ajax({
			url: this.lectioConfig!.autoBackupUrl,
			method: 'post',
			data: JSON.stringify(currentEditorContent),
			dataType: 'json',
			contentType: 'application/json; charset=utf-8',
			success: () => {
				this.autobackupCurrentlyExecutingXhr = null;
				LCDocumentEditor.Log('Autobackup success');
				this.autobackupPreviousEditorContent = currentEditorContent;
				this.ScheduleNextAutobackup();
			},
			error: (jqXHR, textStatic, errorThrown) => {
				this.autobackupCurrentlyExecutingXhr = null;
				const msg = 'Autobackup fejl: ' + textStatic;
				LCDocumentEditor.Log(msg);
				JSErrorHandling.reportJSError(msg, errorThrown);

				this.ScheduleNextAutobackup();
			}
		});
		this.autobackupCurrentlyExecutingXhr = xhr;
		return Promise.race([xhr]);
	}

	lectioConfig: EditorConfig | undefined = undefined;
	autobackupTimeoutHandle: number | null = null;
	autobackupPreviousEditorContent: string | undefined = undefined;
	autobackupCurrentlyExecutingXhr: JQueryXHR | null = null;

	static globalWheelEventsRegistered = false;

	private ScheduleNextAutobackup() {
		this.autobackupTimeoutHandle = setTimeout(() => {
			this.autobackupTimeoutHandle = null;
			this.DoAutobackup();
		}, this.lectioConfig!.autoBackupIntervalSeconds * 1000);
	}

	public ConsiderAutobackupNow(): Promise<undefined> {
		if (this.autobackupCurrentlyExecutingXhr)
			throw new Error('Kan ikke foretage backup nu, der er en igang i forvejen.');
		if (!this.autobackupTimeoutHandle)
			throw new Error('Der er ikke en sat backup-timer.');
		clearTimeout(this.autobackupTimeoutHandle);
		this.autobackupTimeoutHandle = null;
		return this.DoAutobackup();
	}

	private static Log(msg: string) {
		LectioJSUtils.LogDebug(msg);
	}

	public static RestoreAutobackup(
		textareaId: string, lcdocumentid: string, lcdocumenthistoryid: string
	): void {
		const url = LectioJSUtils.GetBaseSchoolURL()
			+ '/lcutil/editorapi.api/GetLCDocumentHistoryContent?lcDocumentId=' + lcdocumentid
			+ '&lcDocumentHistoryId=' + lcdocumenthistoryid;

		LCDocumentEditor.SetEditorContent(textareaId, url);
	}

	public static GetDocumentContent(
		textareaId: string, lcdocumentid: string, ibookStartSectionId: string, ibookEndSectionId: string
	): void {
		const url = LectioJSUtils.GetBaseSchoolURL()
			+ '/lcutil/editorapi.api/GetLCDocumentContent?lcDocumentId=' + lcdocumentid
			+ '&startIbookSectionId=' + ibookStartSectionId
			+ '&endIbookSectionId=' + ibookEndSectionId;

		LCDocumentEditor.SetEditorContent(textareaId, url);
	}

	public static Test_GetCurrentEditorEx(): CKEDITOR.editor {
		const curr = CKEDITOR.currentInstance;
		if (curr)
			return curr;

		const editors = CKEDITOR.instances;
		const editorids = Object.getOwnPropertyNames(editors);
		if (editorids.length === 0)
			throw new Error('Der er ingen editorer');

		if (editorids.length === 1)
			return LectioJSUtils.GetNotNullValue(editors[editorids[0]]);

		// ok, der er aabenbart flere at vaelge imellem...
		const editorid = editorids[0];
		return LectioJSUtils.GetNotNullValue(editors[editorid]);
	}

	private static SetEditorContent(textareaId: string, url: string): void {
		$.ajax({
			url: url,
			method: 'get',
			success: (data: any, textStatus: string, jqXHR: JQueryXHR) => {
				this.Log("SetEditorContent got data");
				const editor = LCDocumentEditor.GetEditorInstanceById(textareaId);
				if (!editor)
					throw new Error('Fandt ikke editor med ID ' + textareaId + '.');
				editor.setData(data, { noSnapshot: true });
			},
			error: (jqXHR: JQueryXHR, textStatic: string, errorThrown) => {
				const msg = 'SetEditorContent fejl: ' + textStatic;
				this.Log(msg);
				JSErrorHandling.reportJSError(msg, errorThrown);
			}
		});
	}

	public static SetEditorContentWithCursorRestore(
		textareaId: string, lcDocumentId: number,
		ibookStartSectionId: string, ibookEndSectionId: string): void {
		LectioJSUtils.AssertNotNullOrEmpty(textareaId, "textareaId");

		if (!lcDocumentId)
			return;

		const url = LectioJSUtils.GetBaseSchoolURL()
			+ '/lcutil/editorapi.api/GetLCDocumentContent?lcDocumentId=' + lcDocumentId
			+ '&startIbookSectionId=' + ibookStartSectionId
			+ '&endIbookSectionId=' + ibookEndSectionId;

		$.ajax({
			url: url,
			method: 'get',
			success: (data: any, textStatus: string, jqXHR: JQueryXHR) => {
				const editor = LCDocumentEditor.GetEditorInstanceById(textareaId);
				if (!editor)
					throw new Error('Fandt ikke editor med ID ' + textareaId + '.');

				const orgSelection = editor.getSelection();
				let orgBookmark: CKEDITOR.dom.bookmark2[];
				if (!orgSelection) {
					const error = "!editor.getSelection()";
					LectioJSUtils.LogError(error);
					JSErrorHandling.reportJSError(error);
				} else
					orgBookmark = orgSelection.createBookmarks2(true);

				const ss = LCCommentBlockUtility.SalvageState(editor);

				editor.setData(data, {
					//internal: true,
					noSnapshot: true,
					callback() {
						LCCommentBlockUtility.RestoreSalvagedState(ss, editor);
						try {
							const selection = editor.getSelection();
							if (selection) {
								if (orgBookmark) {
									selection.selectBookmarks(orgBookmark);
								} else {
									const error = "!orgBookmark efter setData()";
									LectioJSUtils.LogError(error);
									JSErrorHandling.reportJSError(error);
								}
							} else {
								const error = "!editor.getSelection() efter setData()";
								LectioJSUtils.LogError(error);
								JSErrorHandling.reportJSError(error);
							}
						} catch (e: any) {
							LectioJSUtils.LogError(e);
							JSErrorHandling.reportJSError(e);
						}

						editor.resetDirty();
					}
				});
			},
			error: (jqXHR: JQueryXHR, textStatic: string, errorThrown) => {
				const msg = 'SetEditorContent fejl: ' + textStatic;
				LectioJSUtils.LogError(msg);
				JSErrorHandling.reportJSError(msg, errorThrown);
			}
		});
	}

	private static GetEditorInstanceById(textareaId: string): CKEDITOR.editor | undefined {
		const editor = CKEDITOR.instances[textareaId];
		// Findes editoren faktisk i DOM
		if (!$("#" + textareaId).length)
			return undefined;
		return editor;
	}

	static GetEditorLectioState(editor: CKEDITOR.editor & CKEditorLectio): LCDocumentEditorState {
		const state = editor.lectioEditorState;
		if (!state)
			throw new Error('Har ikke lectio editor state.');
		return state;
	}

	static GetEditorEx(editor: CKEDITOR.editor): CKEditorEx {
		return editor;
	}

	public static SaveEditorBookmark(editor: CKEDITOR.editor) {
		if (!editor.focusManager.hasFocus) {
			LectioJSUtils.LogDebug('Skipping editor ' + editor.id + ": no focus.");
			return;
		}

		// Vi holder styr på on editoren har fået focus for meget nylig, da
		// "Gem" knappen sætter focus på editoren, men vi ønsker ikke at
		// genindsætte cursoren hvis det "Gem" knappen der har sat focus på
		// editoren.
		// Hvis der tilgengæld er blevet trykket alt+s, så får vores
		// LectioButton focus, og dermed ønsker vi stadig at gemme bookmark.
		if (editor.focuswassetrecently && !editor.saveexecutedexternally) {
			LectioJSUtils.LogDebug('Skipping editor ' + editor.id + ": focuswassetrecently.");
			return;
		}

		LectioJSUtils.LogDebug('SaveEditorBookmark ' + editor.id);

		const cfg = LCDocumentEditorUtils.GetEditorConfig(editor);

		const input = LectioJSUtils.GetAssertedType(
			document.getElementById(cfg.editorBookmarkField),
			HTMLInputElement, 'html input');
		if (!input)
			return;

		const selection = editor.getSelection();
		if (!selection)
			return;

		const bm = selection.createBookmarks2(true);
		input.value = encodeURIComponent(JSON.stringify(bm));
	}

	public static ClearEditorBookmark(editor: CKEDITOR.editor) {
		LectioJSUtils.LogDebug('ClearEditorBookmark ' + editor.id);

		const cfg = LCDocumentEditorUtils.GetEditorConfig(editor);
		const input = document.getElementById(cfg.editorBookmarkField) as HTMLInputElement;
		if (!input)
			return;

		input.value = '';
	}

	// Reetablerer cursorposition og selection til det den var foer postback.
	public static async InitEditorBookmark(editor: CKEDITOR.editor): Promise<void> {
		const cfg = LCDocumentEditorUtils.GetEditorConfig(editor);

		const input = document.getElementById(cfg.editorBookmarkField);
		if (!input)
			return;
		if (!(input instanceof HTMLInputElement))
			throw new Error('er ikke htmlelement');
		if (!(input && input.value))
			return;

		await LectioJSUtils.Delay(10);

		const bm: CKEDITOR.dom.bookmark[] = JSON.parse(decodeURIComponent(input.value));
		const selection = editor.getSelection();
		if (!selection)
			return;

		// Det her kan gå galt med fejl af typen: Failed to execute
		// 'setStart' on 'Range': The offset 53 is larger than the node's
		// length (21).
		// Vi lader det passere
		selection.selectBookmarks(bm);
		editor.focus();
	}

	public static ConfirmEditorDirtyAndNotSaving(): boolean {
		let bad = false;

		for (const ed of LCDocumentEditorUtils.GetAllEditorInstances()) {
			if (LCDocumentEditor.IsEditorDirtyAndNotSaving(ed)) {
				bad = true;
				break;
			}
		}

		if (bad)
			return confirm(LCDocumentEditor.UnsavedChangesText);
		else
			return true;
	}

	private static IsEditorDirtyAndNotSaving(editor: CKEDITOR.editor | undefined): boolean {
		const enablePrompt = true;
		// IE (11) kører beforeunload mens form.submit() kører. Chrome (43)
		// kører den først efter form.submit() har returneret. onunload kommer
		// ikke med det samme, men først når serveren er klar med svaret (chrome
		// 43).

		const isSaving = PostBackHelper != null && PostBackHelper.IsPerformingPostBack();
		// Ved sessionsudloeb goer vi ikke noget, for der vil hoejest
		// sandsynligt vaere blevet gemt en backup.
		const okUnload = isSaving || (SessionHelper.Instance && SessionHelper.Instance.HasStartedRedirecting());
		LectioJSUtils.LogDebug('issaving: ', isSaving);
		if (enablePrompt && !okUnload && editor && editor.checkDirtyLectio())
			return true;

		return false;
	}

	private static UnsavedChangesText = 'Der er ændringer der ikke er gemt. Ønsker du at forlade siden?';

	private static alwaysSource = LectioClientDevSettings.RegisterSetting('editor', 'source_always_accessible', 'boolean', false);
	private static homeworkboxesinitialvalue: { [name: string]: boolean } = {};

	private static InitializeInstance(
		textareaId: string, lectioConfig: EditorConfig, isDirtyHiddenFieldId: string)
		: LCDocumentEditor {
		window.addEventListener('beforeunload', evt => {
			const winEx: Window & { disableBeforeUnloadPromptAdhoc?: boolean; } = window;
			if (lectioConfig.disableBeforeUnloadPrompt || (winEx.disableBeforeUnloadPromptAdhoc || false))
				return;

			const aTags = document.getElementsByTagName("input");
			const searchTextIsHomework = "edithomework_ishomework";
			const searchTextIsPublic = "edithomework_ispublic"
			const found: { [name: string]: boolean } = {};

			for (let i = 0; i < aTags.length; i++) {
				if (aTags[i].name.includes(searchTextIsHomework) ||
					aTags[i].name.includes(searchTextIsPublic)) {
					const name: string = aTags[i].name;
					const value: boolean = aTags[i].checked;
					found[name] = value;
				}
			}

			let match: boolean = true;
			for (const key in found) {
				if (found[key] !== this.homeworkboxesinitialvalue[key])
					match = false;
			}
			const isSaving = PostBackHelper != null && PostBackHelper.IsPerformingPostBack();
			if (!match && !isSaving) {
				evt.returnValue = "aaaaaaaaa";
				PostBackHelper.LatestSubmitEnded();
			}
			const editor = LCDocumentEditor.GetEditorInstanceById(textareaId);

			if (LCDocumentEditor.IsEditorDirtyAndNotSaving(editor)) {
				evt.returnValue = LCDocumentEditor.UnsavedChangesText;
				PostBackHelper.LatestSubmitEnded();
			}
		});
		const aTags = document.getElementsByTagName("input");
		const searchTextIsHomework = "edithomework_ishomework";
		const searchTextIsPublic = "edithomework_ispublic"

		for (let i = 0; i < aTags.length; i++) {
			if (aTags[i].name.includes(searchTextIsHomework) ||
				aTags[i].name.includes(searchTextIsPublic)) {
				const name: string = aTags[i].name;
				const value: boolean = aTags[i].checked;
				LCDocumentEditor.homeworkboxesinitialvalue[name] = value;
			}
		}

		let pluginsToRemove = "image,oembed,about,wsc,scayt,elementspath,stylescombo,link,mathjax";
		if (!lectioConfig.showSourceArea && !LectioClientDevSettings.GetSettingValue(LCDocumentEditor.alwaysSource)) {
			pluginsToRemove += ",sourcearea";
		}

		const extraPlugins = [
			'attachedfile',
			'headingshortcuts',
			'image2',
			'indentblock',
			'justify',
			'lectioaudio',
			'lectiobuttons',
			'lectiocommentblock2',
			'lectiocontent',
			'lectioembed',
			'lectioEnsureEditElementInView',
			'lectioids',
			'lectioimage',
			'lectiolink',
			'lectiomobiletoolbar',
			'lectiopastehelper',
			'lectioPlaceholderHtml',
			'lectioslide',
			'lectiotexmath',
			'openlink',
			'removeformat',
			'toolbarshortcutsdisplay',
		];

		if (!lectioConfig.enableAutoGrow)
			pluginsToRemove += ",autogrow";
		else if (lectioConfig.enableToolbarFixOnScroll)
			extraPlugins.push(",fixedtoolbar");


		if (!lectioConfig.sharedToolbarCtrlId)
			pluginsToRemove += ",sharedspace";

		let disallowedContent = "";
		if (lectioConfig.rootElement === 'h2')
			disallowedContent = 'h1';
		else if (lectioConfig.rootElement === 'h3')
			disallowedContent = 'h1;h2';

		const iframeheight = 'calc(' + lectioConfig.height + ' - 75px)';

		const config: CKEDITOR.config & { mathJaxClass: string; } = {
			language: 'da',
			removePlugins: pluginsToRemove,
			removeButtons: 'Underline,PasteFromWord,Font,FontSize,HorizontalRule',
			// flg. kan fjernes, naar/hvis vi beslutter helt at droppe mathjax i editoren.
			mathJaxLib: '/lectio/scripts/MathJax/MathJax-2.6.0/MathJax.js?config=TeX-AMS_CHTML',
			mathJaxClass: 'lc-math-tex',
			extraPlugins: extraPlugins.join(','),
			entities: false,
			extraAllowedContent: [
				'a[href]',
				'a[data-lc-display-linktype]',
				'*[id]',
				'span(lc-math-tex)',
				'section(lc-document-slide)',
				'section(lc-display-commentblock)',
				'div(lc-display-commentblock-contents)',
				'div(lc-display-commentblock-errormessage)',
				'*[contenteditable]',
				'cite',
				'blockquote[data-lc-role]',
				'h1[data-lc-header-variant]',
				'h2[data-lc-header-variant]',
				'h3[data-lc-header-variant]',
				'h1[data-lc-header-ispublic]',
				'h2[data-lc-header-ispublic]',
				'h3[data-lc-header-ispublic]',
				'*[data-lc-display-placeholder-text]'
			].join(';'),
			disallowedContent: disallowedContent,
			disableNativeSpellChecker: false,
			width: lectioConfig.width,
			//height: iframeheight,
			//autoGrow_minHeight: iframeheight,
			autoGrow_onStartup: true,
			autoGrow_bottomSpace: 20,
			contentsCss: lectioConfig.cssFiles,
			bodyClass: 'lc-display-fragment',
			imageUploadUrl: LectioJSUtils.GetBaseSchoolURL() + '/lcutil/editorapi.api/posteditorfile',
			uploadUrl: LectioJSUtils.GetBaseSchoolURL() + '/lcutil/editorapi.api/posteditorfile',
			title: false,
			resize_enabled: false,
			// For at makimeret editor kommer ovenpå jquery ui modal dialog, der
			// kører z-index 9995.
			baseFloatZIndex: 10010,
			// Autoupdate slås fra her, men registreres med
			// ScriptManager.RegisterOnSubmitStatement i
			// LCDocumentEditor-kontrollen, for at det også virker i
			// partial-postback
			autoUpdateElement: false,
			format_tags: 'p;h1;h2;h3',
			openlink_modifier: 0,
			openlink_enableReadOnly: true
		};

		if (!lectioConfig.enableAutoGrow) {
			config.height = iframeheight;
		}

		if (lectioConfig.sharedToolbarCtrlId) {
			config.sharedSpaces = {
				top: lectioConfig.sharedToolbarCtrlId
			};
		}

		//toolbar ?: ({ name: string, items?: string[], groups?: string[] } | string)[];
		config.toolbar = [
			{ name: 'lectioButtonsTop', items: ['LectioSave', 'LectioViewMode', '-'] },
			{ name: 'clipboard', groups: ['clipboard', 'undo'], items: ['lectioUndo', 'lectioRedo', 'Cut', 'Copy', 'PasteFromWord', '-'] },
			{ name: 'editing', groups: ['find', 'selection'], items: [] },
			{ name: 'tools', items: ['Maximize'] },
			{ name: 'others', items: ['-'] },
			{ name: 'styles', items: ['Styles', 'LectioFormat', 'LectioToggleFormatButtons'] },
			{ name: 'basicstyles', groups: ['basicstyles', 'cleanup'], items: ['Bold', 'Italic', 'Strike', '-', 'TextColor', 'BGColor', '-', 'Subscript', 'Superscript', '-', 'RemoveFormat', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'] },
			{ name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi'], items: ['-', 'NumberedList', 'BulletedList', '-', 'Outdent', 'Indent'] },
			{ name: 'document', groups: ['mode'], items: ['Source'] },
			'/',
			{ name: 'lectioButtonsBelow', items: ['LectioSlide', '-'] },
			{
				name: 'insert', items: [
					'InsertLabel',
					'LectioToggleInsertButtons',
					'LectioMateriale', 'InsertAttachedFileButton', 'lectiolinkButton', //'lectiounlinkButton',
					'-',
					'Image', 'lectioEmbedButton', 'lectioAudioButton', 'lectioAudioRecordButton',
					'LectioQuiz', 'LectioFritekst',
					'-',
					'Table', 'Lectiotexmath', 'SpecialChar', '-', 'Blockquote'
				]
			},
		];

		// Tilpas ikonerne
		CKEDITOR.skin.addIcon('newslide', '/lectio/img/new-slide.auto');
		CKEDITOR.skin.addIcon('fullscreen', '/lectio/img/screen-full.auto');
		CKEDITOR.skin.addIcon('video', '/lectio/img/video16x16.auto');
		CKEDITOR.skin.addIcon('playaudio', '/lectio/img/audioplay24x24.auto');
		CKEDITOR.skin.addIcon('audio', '/lectio/img/audio16x16.auto');
		CKEDITOR.skin.addIcon('dankort', '/lectio/img/pay_dankort.auto');
		CKEDITOR.skin.addIcon('file', '/lectio/img/file16x16.auto');
		CKEDITOR.skin.addIcon('save', '/lectio/img/save.auto');
		CKEDITOR.skin.addIcon('viewmode', '/lectio/img/readmode.auto');
		CKEDITOR.skin.addIcon('lectioinsertlink', '/lectio/img/link-insert16x16.auto');
		CKEDITOR.skin.addIcon('lectiounlink', '/lectio/img/link-delete16x16.auto');

		const editor: CKEDITOR.editor & CKEditorLectio = lectioConfig.useInlineEditor
			? CKEDITOR.inline(textareaId, config)
			: CKEDITOR.replace(textareaId, config);

		editor.config.specialChars = editor.config.specialChars!.concat([
			'&Alpha;', '&alpha;', '&Beta;', '&beta;', '&Gamma;',
			'&gamma;', '&Delta;', '&delta;', '&Epsilon;', '&epsilon;',
			'&Zeta;', '&zeta;', '&Eta;', '&eta;', '&Theta;', '&theta;',
			'&Iota;', '&iota;', '&Kappa;', '&kappa;', '&Lambda;', '&lambda;',
			'&Mu;', '&mu;', '&Nu;', '&nu;', '&Xi;', '&xi;', '&Omicron;',
			'&omicron;', '&Pi;', '&pi;', '&Rho;', '&rho;', '&Sigma;',
			'&sigma;', '&#x3c2;', '&Tau;', '&tau;', '&Upsilon;', '&upsilon;',
			'&Phi;', '&phi;', '&Chi;', '&chi;', '&Psi;', '&psi;',
			'&Omega;', '&omega;', 'ᴍ', '⇌', '⟶', '⇄']);
		editor.editorConfig = lectioConfig;

		const originalUpdateElement = editor.updateElement;
		editor.updateElement = function () {
			const isDirty = editor.checkDirtyLectio();
			const input = document.getElementById(isDirtyHiddenFieldId) as HTMLInputElement | null;
			if (input) {
				input.value = isDirty ? '1' : '0';
			} else {
				console.warn('Kan ikke finde hidden felt til isDirty.');
			}

			originalUpdateElement.apply(this);
		};

		editor.resetDirtyLectio = () => {
			editor.initiallectiodata = getDataHash(editor);
		};

		let selectionChangeCount = 0;
		let selectionChangeLastStart: CKEDITOR.dom.element | undefined;
		editor.on('selectionChange', () => {
			// CKEditors "floating ui"-toolbar har ikke noget imod at laegge sig
			// oven paa cursoren. Det goer det bare lidt svaert at se hvad man
			// skriver.
			// Her forsoeger vi at detektere naar cursoren kommer derop, og
			// scroller saa lidt op for at goere elementet cursoren staar i,
			// synligt.
			selectionChangeCount++;
			const sel = editor.getSelection();
			if (!sel)
				return;
			const ranges = sel.getRanges();
			if (ranges.length !== 1)
				return;

			const range = ranges[0];
			if (!range.collapsed)
				return;
			const start = range.startContainer instanceof CKEDITOR.dom.element
				? range.startContainer
				: range.startContainer.getParent();
			if (start.hasClass('cke_wysiwyg_div'))
				return;

			// Naar 1) cursor har vaeret i editoren, 2) fokus fjernes, fx ved at
			// trykke paa et toc-element 3) cursor saettes i editoren,
			// kommer der to selectionChangeEvents: foerst for det tidligere
			// fokuserede element og derefter for det ny-fokuserede element. Vi
			// vil naturligvis scrolle til det tidligere fokuserede element.
			// Det forsoeger vi her at undgaa ved at detektere at
			// selectionChange til samme element to gange i traek.
			const last = selectionChangeLastStart;
			selectionChangeLastStart = start;
			if (last && last.$ === start.$)
				return;

			const docrect = editor.container.getClientRect();
			const toolbarheightAppr = 80;
			const isDocPartiallyInvisible = docrect.top < toolbarheightAppr;
			if (!isDocPartiallyInvisible)
				return;

			const rect = start.getClientRect();
			const hiddenElementHeight = toolbarheightAppr - rect.top;
			if (hiddenElementHeight <= 0)
				return;


			const isFirstElementInEditor = start.getParent().getFirst().$ === start.$;
			// Naar der klikkes paa indhold i editoren uden at fokus i forvejen
			// er der, bliver denne eventhandler kaldt, og
			// 'start'/.getSelection() er det foerste element i editoren. Og det
			// oensker vi ikke at scrolle til fuld synlighed.
			// Nedenstaaende betingelse "selectionChangeCount === 1" er en
			// approksimation.
			if (isFirstElementInEditor && selectionChangeCount === 1)
				return;

			const ep = new CKEDITOR.dom.elementPath(start);
			if (!ep.block) {
				// 20181213: .block kan vaere null. Ved ikke hvornaar, men det
				// sker. maaske omkring formler... chrome 71.
				return;
			}
			const epBlockElement = ep.block.getName();
			const additionalScrollForHeaderDropdown =
				epBlockElement === 'h1' ||
					epBlockElement === 'h2' ||
					epBlockElement === 'h3' ? 28 : 0;
			window.scrollBy(0, -1 * (hiddenElementHeight + additionalScrollForHeaderDropdown));
		});

		editor.once('instanceReady', ei => {
			ei.editor.resetDirtyLectio();
			if (lectioConfig.readOnly)
				ei.editor.setReadOnly(true);
		});

		editor.on('getData', (evt: { data: { dataValue: any }, editor: CKEDITOR.editor }) => {
			// Vi koerer editorens filter henover data for at faa fjernet
			// attributter som plugins saetter for at goere deres arbejde, men
			// som ikke skal i databasen. Indfoert for at fjerne
			// headertypevisningsattributten.
			const html = evt.data.dataValue;
			if (!(typeof html === 'string'))
				throw new Error('ikke string');

			// stm 35757: En side der har editorer og bruger LectioButton's
			// ConfirmText, havner hernede lige efter at brugeren bekraefter:
			// Det er via DoPostback() -> ... -> WebForm_OnSubmit() -> ... ->
			// UpdateAllCkEditors -> .... -> (her)
			// Paa det tidspunkt er editoren oftest ikke initialiseret, og fx.
			// editor.filter er derfor ikke sat, saa vi faar en fejl
			// naar vi kalder den. For at undgaa det, hopper vi ud her.
			// Det ville nok vaere paenere at vente med at lave postback, til
			// alle editorer er tilstraekkeligt initaliserede...
			if (evt.editor.status === 'unloaded')
				return;

			const fragment = CKEDITOR.htmlParser.fragment.fromHtml(html);
			editor.filter.applyTo(fragment);

			// 'data-lc-display-placeholder-text' tillades i filteret, for vi
			// saetter den paa html-en inden setData. Det betyder saa at vi er
			// noedt til at fjerne den igen her.
			fragment.forEach(ele => {
				delete ele.attributes['data-lc-display-placeholder-text'];
			}, 1);

			const writer = new CKEDITOR.htmlParser.basicWriter();
			fragment.writeHtml(writer);
			evt.data.dataValue = writer.getHtml(false);
		});

		interface CKRange {
			collapsed: boolean;
		}

		editor.checkDirtyLectio = () => {
			const h = getDataHash(editor);
			return h !== editor.initiallectiodata;
		};

		function getDataHash(ee: CKEDITOR.editor): number {
			const d = ee.getData();
			return StringUtils.HashCode(d);
		}

		// Vil gerne bruge ckeditors maximize-plugin, men vil både have anden
		// position og ikon til den.
		// Derfor forhindrer vi at knappen bliver tilføjet, og tilføjer selv
		// vores variant længere nede ("LectioMaximize").  Tilsvarende med undo
		// og redo.
		const originalAddButton = editor.ui.addButton.bind(editor.ui);
		editor.ui.addButton = (name: string, definition: CKEDITOR.buttonDefinition) => {
			if (definition && definition.command && definition.command in LCDocumentEditorUtils.buttonsToSuppress && name.indexOf('lectio') !== 0) {
				LCDocumentEditorUtils.buttonsToSuppress[definition.command] = definition;
				return;
			}
			originalAddButton.apply(editor, [name, definition]);
		};

		editor.ui.addButton('InsertLabel', {
			label: 'Indsæt:',
			toolbar: 'insert,0',          // place in toolbar
			className: 'cke_toolbar_label',
			click: function () { }         // no-op
		});


		const editorReady = LectioJSUtils.CreateDeferred<CKEDITOR.editor>();
		editor.once('focus', ev => {
			if (lectioConfig.startDimmed) {
				editor.container.removeClass('lc-display-editor-dimmedtemplate');
				//konflikter med placeholder plugin
				//editor.setData("<p>&nbsp;</p>");
				//editor.resetDirty();
			}
		});

		editor.on('focus', ev => {
			//Det her er et hack, se SaveEditorBookmark.
			editor.focuswassetrecently = true;
			setTimeout(() => editor.focuswassetrecently = false, 20);
		});

		editor.once('instanceReady', ev => {
			if (iframeheight !== null)
				$(editor.container.$).find('.cke_contents:first').css('height', iframeheight);

			const editable = editor.editable();
			if (!editable)
				throw new Error('Har ikke editable?!');

			editorReady.resolve(ev.editor);

			//style toolbar
			const config = LCDocumentEditorUtils.GetEditorConfig(ev.editor);
			const toolbar = LCDocumentEditorUtils.GetToolbarObject(ev.editor);

			$(".cke").addClass('lceditornewlayout');
			editable.addClass('lc-display-fragment');

			// Fontikoner: Tilfoej en linie herundre for at angive fontikon.
			// `cmd` for en knap kan ses i devtools ved at inspicere knappen.
			const buttonMods: { cmd: string; label: string | null; fontIcon?: string; fontIconClasses?: string; }[] = [
				{ cmd: 'lectiosave', label: 'Gem', fontIcon: 'save', fontIconClasses: 'bluedark' },
				{ cmd: 'lectioviewmode', label: 'Læs', fontIcon: 'menu_book' },
				{ cmd: 'lectioundo', label: null, fontIcon: 'undo' },
				{ cmd: 'undo', label: null, fontIcon: 'undo' },
				{ cmd: 'lectioredo', label: null, fontIcon: 'redo' },
				{ cmd: 'redo', label: null, fontIcon: 'redo' },
				{ cmd: 'cut', label: null, fontIcon: 'content_cut' },
				{ cmd: 'copy', label: null, fontIcon: 'content_copy' },
				{ cmd: 'lectiotoggleformatbuttons', label: null, fontIcon: 'text_fields', fontIconClasses: 'purple' },
				{ cmd: 'bold', label: null, fontIcon: 'format_bold' },
				{ cmd: 'italic', label: null, fontIcon: 'format_italic' },
				{ cmd: 'strike', label: null, fontIcon: 'format_strikethrough' },
				{ cmd: 'textcolor', label: null, fontIcon: 'format_color_text', fontIconClasses: 'reddark' },
				{ cmd: 'bgcolor', label: null, fontIcon: 'format_ink_highlighter', fontIconClasses: 'yellow' },
				{ cmd: 'subscript', label: null, fontIcon: 'subscript' },
				{ cmd: 'superscript', label: null, fontIcon: 'superscript' },
				{ cmd: 'removeformat', label: null, fontIcon: 'ink_eraser' },
				{ cmd: 'justifyleft', label: null, fontIcon: 'format_align_left' },
				{ cmd: 'justifycenter', label: null, fontIcon: 'format_align_center' },
				{ cmd: 'justifyright', label: null, fontIcon: 'format_align_right' },
				{ cmd: 'justifyblock', label: null, fontIcon: 'format_align_justify' },
				{ cmd: 'numberedlist', label: null, fontIcon: 'format_list_numbered' },
				{ cmd: 'bulletedlist', label: null, fontIcon: 'format_list_bulleted' },
				{ cmd: 'outdent', label: null, fontIcon: 'format_indent_decrease' },
				{ cmd: 'indent', label: null, fontIcon: 'format_indent_increase' },
				{ cmd: 'lectioslide', label: 'Slide', fontIcon: 'contextual_token_add', fontIconClasses: 'orange' },
				{ cmd: 'lectiotoggleinsertbuttons', label: null, fontIcon: 'docs_add_on', fontIconClasses: 'greendark' },
				{ cmd: 'lectiomateriale', label: 'Materiale', fontIcon: 'add_circle', fontIconClasses: 'greendark' },
				{ cmd: 'insertattachedfilebutton', label: 'Fil', fontIcon: 'description' },
				{ cmd: 'lectiolinkbutton', label: 'Link', fontIcon: 'open_in_new', fontIconClasses: 'bluedark' },
				{ cmd: 'lectioinsertlink', label: null, fontIcon: 'open_in_new', fontIconClasses: 'bluedark' },
				{ cmd: 'image', label: 'Billede', fontIcon: 'image' },
				{ cmd: 'lectioembedbutton', label: 'Video', fontIcon: 'smart_display' },
				{ cmd: 'lectioaudiobutton', label: 'Lyd', fontIcon: 'music_note' },
				{ cmd: 'lectioquiz', label: 'Quiz', fontIcon: 'quiz' },
				{ cmd: 'lectiofritekst', label: 'Afsnit', fontIcon: 'note_add', fontIconClasses: 'greendark' },
				{ cmd: 'table', label: 'Tabel', fontIcon: 'table' },
				{ cmd: 'lectiotexmath', label: 'Formel', fontIcon: 'functions' },
				{ cmd: 'specialchar', label: 'Symbol', fontIcon: 'special_character' },
				{ cmd: 'blockquote', label: null, fontIcon: 'format_quote' },
			];

			const buttons = toolbar.find('.cke_button').toArray();
			const byName = new Map<string, HTMLElement>();

			for (const btn of buttons) {
				const cmd = btn.classList
					.firstOrDefault(c => c.startsWith('cke_button__'))
					?.substring('cke_button__'.length);
				if (!cmd)
					continue;
				byName.set(cmd, btn);
			}

			for (const mod of buttonMods) {
				const btn = byName.get(mod.cmd);
				if (!btn) {
					console.warn(`Fandt ikke knap for cmd ${mod.cmd}.`)
					continue;
				}

				if (mod.fontIcon) {
					btn.querySelector(':scope > .cke_button_icon')?.remove();
				}

				const moreClassse = mod.fontIconClasses
					? mod.fontIconClasses.replace(/(^| )(\w)/g, ' ls-fonticon-$2')
					: '';

				const fi = mod.fontIcon ? `<span class='cke_button_icon ls-fonticon${moreClassse}'>${mod.fontIcon}</span>` : '';

				if (mod.label)
					$(btn).append(`${fi}<span>${mod.label}</span>`);
				else
					$(btn).append(fi);
			}

			const buttonSlide = $(".cke_button__lectioslide ", toolbar);

			// fix tooltips
			$(".cke_button__lectiotexmath", toolbar).prop('title', 'Indsæt formel');
			{
				const statusText = config.lastSaveDateTime ? 'Senest gemt kl. ' + config.lastSaveDateTime : '';

				const ss = $("<span class='lctoolbarsavestatus'>" + statusText + "</span>");

				//skal placeres til højre. float virker ikke rigtigt...
				const x = buttonSlide.length === 0 ? 0 : buttonSlide.parent().outerWidth(true)!;
				ss.css('margin-left', (100 - x) + "px");
			}
			const item = editor.ui.get('InsertLabel') as any;
			if (item) item.setState(CKEDITOR.TRISTATE_DISABLED); // non-interactive

			LectioPageOps.GetScrollPositionRestoredPromise()
				.then(() => LCDocumentEditor.InitEditorBookmark(ev.editor));
			LectioPageOps.ResolveRestoreScrollDependency(textareaId);
			if (config.onEditorReady)
				config.onEditorReady(ev.editor);
		});

		editor.on('blur', ei => {
			this.ClearEditorBookmark(ei.editor);
		});

		editor.on('dataReady', ev => {
			// inline
			if (!editor.plugins.magicline.backdoor)
				return;
			const triggers = editor.plugins.magicline.backdoor.that.triggers;

			// Aktiverer magic line på slide-elementerne. Ideelt set ville den
			// nok kun være aktiveret på section.lc-document-slide.
			triggers.section = 1;

			// Disse virker overfloedige, saeligt paa slides.
			delete triggers.ul;
			delete triggers.ol;
		});

		// Slaaet fra, da IE (11) og safari ikke kan finde ud af det.
		const enablePasteContent = false;
		if (enablePasteContent) {
			// Haandter indsaet af billede fra browser (i det mindste i chrome
			// windows):
			// Hvis vi ikke goer noget, bliver billedet blot indsat som html
			// img. Vi vil ogsaa gerne have fat i billedet.
			// For at goere det, skal vi soerge for at event-objektet for
			// ckeditors paste-event ser "korrekt" ud, saa
			// ckeditor/uploadwidget tager haand om resten.
			editor.on('beforePaste', evt => {
				// Haandterer beforePaste (selv om den er deprecated i ckeditor
				// 4.5 :-|), fordi paste er asynkron, saa der er billedet tabt.
				// CKEditor goer noget lignende ved at kalde
				// eventData.dataTransfer.cacheData() inden den rejser
				// paste-event
				// (og umiddelbart inden beforePaste).
				// Den foretraekker imidlertid det foerste element i browserens
				// dataTransfer.items, hvilket (i chrome 43 windows) er html.
				// Elementet med indeks 1 er et DataTransferItem, hvorfra vi kan
				// faa en blob.
				const data = evt.data;
				// "$"-property er browserens eget objekt, og "_" er en form for
				// ckeditor-skyggedata, der fyldes ud af cacheData.
				// "$" er ikke sat i IE (11).
				if (!data.dataTransfer.$)
					return;
				const nativeItems = data.dataTransfer.$.items;
				if (nativeItems && nativeItems.length === 2 && nativeItems[0].kind === 'string' && nativeItems[1].kind === 'file') {
					const blob = nativeItems[1].getAsFile();
					data.dataTransfer._.files = [blob];
				}
			});
			type BlobLectioExt = Blob & { lectioFilename?: string };
			editor.on('paste', evt => {
				// Hvis der er en fil, vil vi gerne have indsat den, men
				// hvis vi ikke rydder dataValue, er den den html der bliver indsat.
				const data = evt.data;
				LectioJSUtils.LogDebug(data.dataValue);
				if (data.dataValue && data.dataTransfer._.files && data.dataTransfer._.files.length === 1) {
					// Vi vil proeve at faa filnavnet med, saa billederne ikke
					// alle kommer til at hedde "image.jpg" o.l..
					// Det graver vi ud af html-en.
					const html = data.dataValue;
					const m = html.match(/src="([^"]+)"/);
					const url = m ? m[1] : '';
					const m2 = url.match(/([\w.+%]+)$/);
					const filename = m2 ? m2[1] : '';

					if (filename) {
						const theBlob = data.dataTransfer._.files[0] as BlobLectioExt;
						// Vi gemmer filnavnet direkte paa blob-en, saa vi i
						// vores override af uploadRepository.create kan grave
						// den frem igen.
						theBlob.lectioFilename = filename;
					}

					data.dataValue = '';
				}

				// Denne handler registreres med prioritet 7, saa vi kommer foer
				// uploadwidget, men efter diverse andre behandlinger af
				// clipboarddata.
			}, null, null, 7);

			editor.on('instanceReady', ev => {
				const rep = editor.uploadRepository!;
				const origCreate = rep.create.bind(rep);
				rep.create = (fileOrData: BlobLectioExt, filename: string) => {
					const lectioFilename = fileOrData.lectioFilename;
					if (lectioFilename && !filename) {
						return origCreate(fileOrData, lectioFilename);
					}
					return origCreate(fileOrData, filename);
				};
			});

		}

		// er lige nødt til at fange noget snavs fra Safari
		editor.on('paste', evt => {
			const data = evt.data;
			if (/webkit-fake-url/i.test(data.dataValue))
				alert('Hov! Du prøver at indsætte et billede/en fil, men din browser (Safari) vil ikke være med, og laver i stedet et link til en lokal fil, som andre derfor ikke kan se. Fjern derfor billedet/filen og benyt upload knappen eller en anden browser i stedet for.');
			if (/Temp\/msohtml/i.test(data.dataValue)) //(18/1-2017) STM 30146: Det sker i alle browsere undtagen IE 11
				alert('Hov! Du prøver at indsætte et billede/en fil, men din browser vil ikke være med, og laver i stedet et link til en lokal fil, som andre derfor ikke kan se. Fjern derfor billedet/filen og benyt upload knappen.');
		}, null, null, 7);

		// LCDocumentEditorState
		{
			const getFocusIsInEditor = () => {
				const container = LectioJSUtils.GetAssertedType(
					editor.ui.contentsElement.$, HTMLElement, 'htmlelement');
				const isInEditor = $(LectioJSUtils.GetAssertedType(
					document.activeElement, Element, 'Element'))
					.parents().toArray().indexOf(container) !== -1;
				return isInEditor;
			};

			let focusIsInEditor: boolean | null = null;
			editor.on('beforeCommandExec', evt => {
				focusIsInEditor = getFocusIsInEditor();
			});
			const getFocusWasInEditor = () => {
				if (focusIsInEditor == null)
					throw new Error('focusIsInEditor is null.');
				// Smider den gemte vaerdi vaek, for nemmere at opdage hvis
				// beforeCommandExec ikke bliver kaldt hver gang.
				const rv = focusIsInEditor;
				focusIsInEditor = null;
				return rv;
			};

			const state: LCDocumentEditorState = {
				getWasFocusInEditorBeforeCommand: () => getFocusWasInEditor()
			};
			editor.lectioEditorState = state;
		}

		const inst = new LCDocumentEditor(editor, editorReady.promise());
		return inst;
	}

	static firstEditorReady = LectioJSUtils.CreateDeferred<LCDocumentEditor>();
	static allEditorsReady = LectioJSUtils.CreateDeferred<void>();
	private static editorStartedInitCount: number = 0;
	private static editorFinishedInitCount: number = 0;

	public static init(editorId: string, config: EditorConfig, isDirtyHiddenFieldId: string, year: number) {
		LCDocumentEditor.ConfigureCKEditorGlobal(config, year);
		const inst = LCDocumentEditor.InitializeInstance(editorId, config, isDirtyHiddenFieldId);
		inst.SetupAutoBackup(config);
		this.editorStartedInitCount++;
		LectioJSUtils.LogDebug('ckeditor: init');

		inst.editorReady.then(() => {
			this.editorFinishedInitCount++;
			LectioJSUtils.LogDebug('ckeditor: init, .editorready');
			LCDocumentEditor.firstEditorReady.resolve(inst);
			if (this.editorFinishedInitCount === this.editorStartedInitCount) {
				this.allEditorsReady.resolve();
			}
		});
	}

	public static ExecSaveCommandOnFirstInstance() {
		let editorInstance: CKEDITOR.editor | null = null;
		if (CKEDITOR.currentInstance) {
			editorInstance = CKEDITOR.currentInstance;
		} else {
			for (const inst of LCDocumentEditorUtils.GetAllEditorInstances()) {
				editorInstance = inst;
				break;
			}
		}

		if (editorInstance) {
			//hack
			editorInstance.saveexecutedexternally = true;
			editorInstance.execCommand('lectiosave');
			editorInstance.saveexecutedexternally = false;
		}
	}
}
